import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CloudArrowUp,
  DownloadSimple,
  GoogleLogo,
  Info,
  LinkSimpleBreak,
  Check,
  Warning,
  Plug,
} from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { PasswordInput } from '../ui/password-input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LS_AUTOPC_KEY = 'erp_backup_auto_pc_download';
const LS_LAST_PC_KEY = 'erp_backup_last_pc_download_id';

/**
 * BackupSettingsCard — combined controls for:
 *  1. "Auto-download new backups to my PC" (client-side, no OAuth)
 *  2. Per-user Google Drive connection (each user brings their own OAuth
 *     Client ID + Secret; backups auto-upload to that user's Drive folder).
 *
 * Props:
 *   backups     — the list from parent (used to detect newest one for auto-DL)
 *   onRefresh   — parent refresh callback (called after connect/disconnect)
 *   downloadBackup(backup) — parent helper that performs the actual ZIP download
 */
export default function BackupSettingsCard({ backups = [], onRefresh, downloadBackup }) {
  const [autoPC, setAutoPC] = useState(() => localStorage.getItem(LS_AUTOPC_KEY) === '1');

  // Drive state
  const [driveStatus, setDriveStatus] = useState(null);
  const [loadingDrive, setLoadingDrive] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [form, setForm] = useState({ client_id: '', client_secret: '', folder_name: 'ERP Hub Backups' });

  const token =
    localStorage.getItem('erp_token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const redirectUri = `${API_URL}/api/drive/callback`;

  const loadStatus = useCallback(async () => {
    setLoadingDrive(true);
    try {
      const r = await axios.get(`${API_URL}/api/drive/status`, { headers });
      setDriveStatus(r.data);
    } catch (e) {
      setDriveStatus({ connected: false });
    } finally {
      setLoadingDrive(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    // If Google redirected us back with ?drive_connected=1, celebrate & clean URL
    if (new URLSearchParams(window.location.search).get('drive_connected') === '1') {
      toast.success('Google Drive connected!');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(loadStatus, 500);
    }
  }, [loadStatus]);

  // -----------------------------------------------------------
  // Auto-download to PC — runs whenever the backups list updates
  // -----------------------------------------------------------
  useEffect(() => {
    if (!autoPC) return;
    if (!backups || backups.length === 0) return;
    const latest = [...backups]
      .filter(b => b.status === 'completed')
      .sort((a, b) => new Date(b.completed_at || b.created_at) - new Date(a.completed_at || a.created_at))[0];
    if (!latest) return;
    const lastId = localStorage.getItem(LS_LAST_PC_KEY);
    if (lastId === latest.id) return;
    // Download once and remember
    localStorage.setItem(LS_LAST_PC_KEY, latest.id);
    if (typeof downloadBackup === 'function') {
      downloadBackup(latest);
      toast.success(`Auto-downloaded ${latest.filename} to your PC`);
    }
  }, [backups, autoPC, downloadBackup]);

  // -----------------------------------------------------------
  // Actions
  // -----------------------------------------------------------
  const toggleAutoPC = (checked) => {
    setAutoPC(checked);
    localStorage.setItem(LS_AUTOPC_KEY, checked ? '1' : '0');
    if (checked) toast.info('You will get every new backup ZIP downloaded to your PC automatically.');
  };

  const saveCredentialsAndAuthorize = async () => {
    if (!form.client_id.trim() || !form.client_secret.trim()) {
      toast.error('Client ID and Client Secret are required');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/drive/save-credentials`,
        {
          client_id: form.client_id.trim(),
          client_secret: form.client_secret.trim(),
          redirect_uri: redirectUri,
          folder_name: form.folder_name.trim() || 'ERP Hub Backups',
        },
        { headers },
      );
      const auth = await axios.get(`${API_URL}/api/drive/authorize`, { headers });
      setSetupOpen(false);
      // Open Google consent in a new tab; callback redirects back here.
      window.open(auth.data.authorization_url, '_blank', 'noopener');
      toast.info('Approve access in the new tab. This page will refresh once you return.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm('Disconnect Google Drive? Backups will stop uploading to your Drive.')) return;
    try {
      await axios.post(
        `${API_URL}/api/drive/disconnect`,
        { revoke_remote: false },
        { headers },
      );
      toast.success('Google Drive disconnected');
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Disconnect failed');
    }
  };

  const uploadLatestToDrive = async () => {
    const latest = [...backups]
      .filter(b => b.status === 'completed')
      .sort((a, b) => new Date(b.completed_at || b.created_at) - new Date(a.completed_at || a.created_at))[0];
    if (!latest) { toast.info('No completed backup yet'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/drive/upload/${latest.id}`, {}, { headers });
      toast.success('Uploaded to Google Drive');
      if (r.data.drive_view_link) window.open(r.data.drive_view_link, '_blank');
      onRefresh && onRefresh();
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* -------- 1. Auto-download to PC -------- */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500 shrink-0">
              <DownloadSimple size={20} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold">Auto-download to my PC</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    When a new backup completes, it will be downloaded to your PC&apos;s default Downloads folder automatically. Nothing is uploaded anywhere.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 shrink-0 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={autoPC}
                    onChange={(e) => toggleAutoPC(e.target.checked)}
                    className="h-4 w-4 accent-primary"
                    data-testid="backup-auto-pc-toggle"
                  />
                  <span className="text-xs font-medium">{autoPC ? 'ON' : 'OFF'}</span>
                </label>
              </div>
              {autoPC && (
                <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-1">
                  <Info size={11} /> Runs when you open this page (browsers block truly background downloads).
                </p>
              )}
            </div>
          </div>
        </div>

        {/* -------- 2. Google Drive (per-user) -------- */}
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-500 shrink-0">
              <GoogleLogo size={20} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold flex items-center gap-2">
                    Google Drive (my account)
                    {driveStatus?.connected && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600">
                        <Check size={10} /> Connected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {driveStatus?.connected ? (
                      <>Uploading to <span className="font-medium">{driveStatus.folder_name}</span>
                        {driveStatus.email ? <> as <span className="font-medium">{driveStatus.email}</span></> : null}</>
                    ) : (
                      <>Auto-upload every new backup to your personal Google Drive. Each user uses their own Google account.</>
                    )}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                {!driveStatus?.connected ? (
                  <>
                    <Button size="sm" onClick={() => setSetupOpen(true)} data-testid="drive-connect-btn">
                      <Plug size={14} className="mr-1.5" /> Connect Drive
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setHelpOpen(true)}>
                      <Info size={14} className="mr-1.5" /> How do I get my keys?
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={uploadLatestToDrive} data-testid="drive-upload-latest">
                      <CloudArrowUp size={14} className="mr-1.5" /> Upload latest now
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={disconnectDrive} data-testid="drive-disconnect-btn">
                      <LinkSimpleBreak size={14} className="mr-1.5" /> Disconnect
                    </Button>
                    {driveStatus.last_upload_at && (
                      <span className="text-[10px] text-muted-foreground">
                        Last upload: {new Date(driveStatus.last_upload_at).toLocaleString()}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== Setup Dialog ==================== */}
      <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Connect your Google Drive</DialogTitle>
            <DialogDescription className="text-xs">
              Each user brings their own Google Cloud OAuth credentials — that way your backups go straight to your personal Drive and no super-admin has access. Follow the setup guide below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 p-2.5 text-[11px] text-amber-800 dark:text-amber-200">
              <Warning size={12} className="inline mr-1" />
              You need to whitelist this exact redirect URI in your Google Cloud OAuth client:
              <div className="font-mono text-[10px] mt-1 p-1.5 bg-background/60 rounded break-all">{redirectUri}</div>
            </div>

            <div>
              <Label className="text-xs">Google OAuth Client ID</Label>
              <Input
                value={form.client_id}
                onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                placeholder="xxxxxxxxx-xxx.apps.googleusercontent.com"
                data-testid="drive-client-id"
              />
            </div>
            <div>
              <Label className="text-xs">Google OAuth Client Secret</Label>
              <PasswordInput
                value={form.client_secret}
                onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                placeholder="GOCSPX-xxxxxxxxxxxxxxxx"
                data-testid="drive-client-secret"
              />
            </div>
            <div>
              <Label className="text-xs">Drive folder name (backups will be placed here)</Label>
              <Input
                value={form.folder_name}
                onChange={(e) => setForm({ ...form, folder_name: e.target.value })}
                placeholder="ERP Hub Backups"
                data-testid="drive-folder-name"
              />
            </div>

            <button
              className="w-full text-left text-xs text-primary underline decoration-dotted"
              onClick={() => { setSetupOpen(false); setHelpOpen(true); }}
            >
              Need help getting the Client ID / Secret? Show me the 4-step guide →
            </button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSetupOpen(false)}>Cancel</Button>
            <Button onClick={saveCredentialsAndAuthorize} data-testid="drive-save-authorize">
              Save & Continue to Google
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== Help Dialog ==================== */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>How to get your Google Drive OAuth credentials</DialogTitle>
            <DialogDescription className="text-xs">
              One-time setup, about 5–10 minutes. All values stay in your own Google account.
            </DialogDescription>
          </DialogHeader>

          <ol className="space-y-3 text-sm py-2 list-decimal pl-5">
            <li>
              Open <a className="text-primary underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">console.cloud.google.com</a> and create a new project (any name, e.g. &ldquo;My ERP Backups&rdquo;).
            </li>
            <li>
              <strong>APIs &amp; Services → Library</strong> → search &ldquo;<strong>Google Drive API</strong>&rdquo; → click <strong>Enable</strong>.
            </li>
            <li>
              <strong>APIs &amp; Services → OAuth consent screen</strong>:
              <ul className="list-disc pl-5 mt-1 space-y-1 text-xs text-muted-foreground">
                <li>User type: <strong>External</strong></li>
                <li>App name: anything (e.g. &ldquo;ERP Backup&rdquo;), your email as support &amp; developer contact</li>
                <li>Under <strong>Scopes</strong> → add <code>https://www.googleapis.com/auth/drive.file</code></li>
                <li>Under <strong>Test users</strong> → add your own Google email (this is important for personal accounts)</li>
              </ul>
            </li>
            <li>
              <strong>APIs &amp; Services → Credentials → Create Credentials → OAuth client ID</strong>:
              <ul className="list-disc pl-5 mt-1 space-y-1 text-xs text-muted-foreground">
                <li>Application type: <strong>Web application</strong></li>
                <li>Authorized redirect URIs — paste this exact value:</li>
                <li className="list-none"><code className="text-[10px] bg-muted p-1 rounded break-all">{redirectUri}</code></li>
                <li>Click <strong>Create</strong> → copy the <strong>Client ID</strong> and <strong>Client Secret</strong>.</li>
              </ul>
            </li>
            <li>
              Come back here → click &ldquo;Connect Drive&rdquo; → paste Client ID and Secret → click &ldquo;Save &amp; Continue to Google&rdquo; → approve access in the new tab.
            </li>
          </ol>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHelpOpen(false)}>Close</Button>
            <Button onClick={() => { setHelpOpen(false); setSetupOpen(true); }} data-testid="drive-help-open-setup">
              I have my keys — open setup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
